import {
	ITrackingDB,
	ITrackingSummaryDB,
	IContentSummaryDB,
	ITrendItemDB,
	IHighlightContentDB,
	IContentRankingResponseDB,
	ICreatorRankingResponseDB,
	ITrendingContentResponseDB,
	ITrackingCreatorContentDB,
	IInfluencerDB,
	ITrendParams,
	IHighlightParams,
	IRankingFilterParams,
	ICreatorRankingFilterParams,
	ITrendingContentParams,
} from 'type/tracking';

import { api } from './api';

export const wconcept_tracking_apis = {
	// 트래킹 목록
	// GET /api/tracking/
	getTrackingList: async () => {
		const response = await api.get('api/tracking/');
		return response.data.trackings as ITrackingDB[];
	},

	// 트래킹 요약
	// GET /api/tracking/{code}/summary
	getTrackingSummary: async (code: string, startDate?: string, endDate?: string) => {
		const params = new URLSearchParams();
		if (startDate) params.append('startDate', startDate);
		if (endDate) params.append('endDate', endDate);
		const query = params.toString() ? `?${params.toString()}` : '';
		const response = await api.get(`api/tracking/${code}/summary${query}`);
		return response.data as ITrackingSummaryDB;
	},

	// 콘텐츠 요약
	// GET /api/tracking/{code}/content-summary
	getContentSummary: async (code: string, startDate?: string, endDate?: string) => {
		const params = new URLSearchParams();
		if (startDate) params.append('startDate', startDate);
		if (endDate) params.append('endDate', endDate);
		const query = params.toString() ? `?${params.toString()}` : '';
		const response = await api.get(`api/tracking/${code}/content-summary${query}`);
		return response.data as IContentSummaryDB;
	},

	// 성과 추이
	// GET /api/tracking/{code}/trend
	getTrackingTrend: async (code: string, params: ITrendParams) => {
		const searchParams = new URLSearchParams();
		searchParams.append('metric', params.metric);
		searchParams.append('aggregationType', params.aggregationType);
		if (params.contentTypes?.length) {
			params.contentTypes.forEach((ct) => searchParams.append('contentTypes', ct));
		}
		if (params.startDate) searchParams.append('startDate', params.startDate);
		if (params.endDate) searchParams.append('endDate', params.endDate);
		const response = await api.get(
			`api/tracking/${code}/trend?${searchParams.toString()}`,
		);
		return response.data.trend as ITrendItemDB[];
	},

	// 하이라이트 콘텐츠
	// GET /api/tracking/{code}/highlights
	getTrackingHighlights: async (code: string, params: IHighlightParams) => {
		const searchParams = new URLSearchParams();
		searchParams.append('metric', params.metric);
		if (params.contentTypes?.length) {
			params.contentTypes.forEach((ct) => searchParams.append('contentTypes', ct));
		}
		if (params.date) searchParams.append('date', params.date);
		if (params.limit) searchParams.append('limit', params.limit.toString());
		const response = await api.get(
			`api/tracking/${code}/highlights?${searchParams.toString()}`,
		);
		return response.data.highlights as IHighlightContentDB[];
	},

	// 콘텐츠 랭킹
	// GET /api/tracking/{code}/content-ranking
	getContentRanking: async (code: string, params: IRankingFilterParams) => {
		const searchParams = new URLSearchParams();
		if (params.startDate) searchParams.append('startDate', params.startDate);
		if (params.endDate) searchParams.append('endDate', params.endDate);
		if (params.contentTypes?.length) {
			params.contentTypes.forEach((ct) => searchParams.append('contentTypes', ct));
		}
		if (params.followerMin !== undefined)
			searchParams.append('followerMin', params.followerMin.toString());
		if (params.followerMax !== undefined)
			searchParams.append('followerMax', params.followerMax.toString());
		if (params.influencerCode?.length) {
			params.influencerCode.forEach((ic) => searchParams.append('influencerCode', ic));
		}
		if (params.orderBy) searchParams.append('orderBy', params.orderBy);
		if (params.page) searchParams.append('page', params.page.toString());
		if (params.pageSize) searchParams.append('pageSize', params.pageSize.toString());
		const response = await api.get(
			`api/tracking/${code}/content-ranking?${searchParams.toString()}`,
		);
		return response.data as IContentRankingResponseDB;
	},

	// 크리에이터 랭킹
	// GET /api/tracking/{code}/creator-ranking
	getCreatorRanking: async (code: string, params: ICreatorRankingFilterParams) => {
		const searchParams = new URLSearchParams();
		if (params.viewMode) searchParams.append('viewMode', params.viewMode);
		if (params.startDate) searchParams.append('startDate', params.startDate);
		if (params.endDate) searchParams.append('endDate', params.endDate);
		if (params.contentTypes?.length) {
			params.contentTypes.forEach((ct) => searchParams.append('contentTypes', ct));
		}
		if (params.followerMin !== undefined)
			searchParams.append('followerMin', params.followerMin.toString());
		if (params.followerMax !== undefined)
			searchParams.append('followerMax', params.followerMax.toString());
		if (params.influencerCode?.length) {
			params.influencerCode.forEach((ic) => searchParams.append('influencerCode', ic));
		}
		if (params.orderBy) searchParams.append('orderBy', params.orderBy);
		if (params.page) searchParams.append('page', params.page.toString());
		if (params.pageSize) searchParams.append('pageSize', params.pageSize.toString());
		const response = await api.get(
			`api/tracking/${code}/creator-ranking?${searchParams.toString()}`,
		);
		return response.data as ICreatorRankingResponseDB;
	},

	// 급상승 콘텐츠
	// GET /api/tracking/{code}/trending-contents
	getTrendingContents: async (code: string, params?: ITrendingContentParams) => {
		const searchParams = new URLSearchParams();
		if (params?.metric) searchParams.append('metric', params.metric);
		if (params?.contentTypes?.length) {
			params.contentTypes.forEach((ct) => searchParams.append('contentTypes', ct));
		}
		if (params?.startDate) searchParams.append('startDate', params.startDate);
		if (params?.endDate) searchParams.append('endDate', params.endDate);
		if (params?.page) searchParams.append('page', params.page.toString());
		if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
		const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
		const response = await api.get(`api/tracking/${code}/trending-contents${query}`);
		return response.data as ITrendingContentResponseDB;
	},

	// 크리에이터별 콘텐츠
	// GET /api/tracking/{code}/creator-ranking/{influencerCode}/contents
	getTrackingCreatorContents: async (code: string, influencerCode: string) => {
		const response = await api.get(
			`api/tracking/${code}/creator-ranking/${influencerCode}/contents`,
		);
		return response.data.contents as ITrackingCreatorContentDB[];
	},

	// 필터용 계정/채널 목록
	// GET /api/tracking/{code}/influencers
	getInfluencers: async (code: string) => {
		const response = await api.get(`api/tracking/${code}/influencers`);
		return response.data.influencers as IInfluencerDB[];
	},

	// 콘텐츠 랭킹 엑셀 export
	// GET /api/tracking/{code}/content-ranking/export
	getContentRankingExport: async (code: string, params: IRankingFilterParams) => {
		const searchParams = new URLSearchParams();
		if (params.startDate) searchParams.append('startDate', params.startDate);
		if (params.endDate) searchParams.append('endDate', params.endDate);
		if (params.contentTypes?.length) {
			params.contentTypes.forEach((ct) => searchParams.append('contentTypes', ct));
		}
		if (params.influencerCode?.length) {
			params.influencerCode.forEach((ic) => searchParams.append('influencerCode', ic));
		}
		const response = await api.get(
			`api/tracking/${code}/content-ranking/export?${searchParams.toString()}`,
			{ responseType: 'blob' },
		);
		const blob = new Blob([response.data], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		});
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `content-ranking-${code}.xlsx`;
		a.click();
		URL.revokeObjectURL(url);
	},
};
